<div class="container" [formGroup]="form">
  <qai-item-grid
    [repeatCount]="'auto-fit'"
    [gap]="'12px'"
    [minItemWidth]="'200px'"
  >
    <qai-field [label]="'simulationBuilder.ASSIGNMENT_TYPE_LABEL' | transloco">
      <qai-floating-select
        [options]="assignmentTypeOptions"
        [placeholder]="'simulationBuilder.SELECT_ASSIGNMENT_TYPE_PLACEHOLDER' | transloco"
        [placement]="'below-start'"
        [offset]="4"
        [shape]="'rectangle'"
        [value]="form.get('moduleAssignmentType')?.value"
        (selectionChange)="
          form.get('moduleAssignmentType')?.setValue($event?.value)
        "
        [disabled]="readOnly"
      ></qai-floating-select>
    </qai-field>

    <qai-field
      [label]="'simulationBuilder.NAME_LABEL' | transloco"
      [error]="
        isAssignmentNameAlreadyExists
          ? ('simulationBuilder.DUPLICATE_NAME_ERROR_ASSIGNMENT' | transloco)
          : ''
      "
    >
      <input
        qai-input
        type="text"
        [placeholder]="'simulationBuilder.ASSIGNMENT_NAME_PLACEHOLDER' | transloco"
        formControlName="moduleAssignmentName"
      />
    </qai-field>

    <qai-field
      [label]="(isCertificationType() ? 'simulationBuilder.PASSING_SCORE_LABEL' : 'simulationBuilder.PASSING_SCORE_OPTIONAL_LABEL') | transloco"
      [error]="
        form.get('scoreCriteria')?.hasError('maxExceeded')
          ? ('simulationBuilder.SCORE_MAX_EXCEEDED_ERROR' | transloco)
          : ''
      "
    >
      <input
        qai-input
        type="number"
        formControlName="scoreCriteria"
        min="0"
        max="5"
        [disabled]="readOnly"
      />

      <button
        #floatingRefHelp
        qaiSlot="endIcon"
        qai-icon-button
        icon="help-circle"
        kind="quaternary"
      >
        {{ 'simulationBuilder.GET_HELP_BUTTON' | transloco }}
      </button>
    </qai-field>

    <qai-tooltip
      [qaiFloating]="floatingRefHelp.el.nativeElement"
      [placement]="'above-end'"
      [events]="['hover']"
      [style.maxWidth]="'320px'"
    >
      {{ 'simulationBuilder.PASSING_SCORE_TOOLTIP' | transloco }}
    </qai-tooltip>
  </qai-item-grid>

  @if (!shouldHideFieldsForPersonalizedPractice()) {
    <qai-field [label]="'simulationBuilder.DESCRIPTION_LABEL' | transloco">
      <textarea
        qai-textarea
        autosize
        [maxLines]="5"
        [placeholder]="'simulationBuilder.ASSIGNMENT_DESCRIPTION_PLACEHOLDER' | transloco"
        formControlName="description"
      ></textarea>
    </qai-field>
  }

  <qai-item-grid
    [repeatCount]="'auto-fit'"
    [gap]="'45px'"
    [minItemWidth]="'200px'"
  >
    @if (!shouldHideFieldsForPersonalizedPractice()) {
      <qai-field [label]="''">
        <span qaiSlot="label" class="label-with-icon">
          <span>{{ 'simulationBuilder.MESSAGING_FEEDBACK_LABEL' | transloco }}</span>
          <button #floatingRef type="button" class="trailing-icon-button">
            <qai-icon [kind]="'help-circle'" size="16px" />
          </button>

          <qai-tooltip
            [qaiFloating]="floatingRef"
            [placement]="'below-start'"
            [events]="['hover']"
            [style.maxWidth]="'320px'"
          >
            <div class="tooltip-heading">{{ 'simulationBuilder.MESSAGING_FEEDBACK_TOOLTIP_HEADING' | transloco }}</div>
            {{ 'simulationBuilder.MESSAGING_FEEDBACK_TOOLTIP_BODY' | transloco }}
          </qai-tooltip>
        </span>

        <qai-toggle-switch-wrapper
          [checked]="showMissedFactsChecked"
          (checkedChange)="onshowMissedFactsChange($event)"
          [enabledText]="'simulationBuilder.ENABLED_TEXT' | transloco"
          [disabledText]="'simulationBuilder.DISABLED_TEXT' | transloco"
          size="small"
          shape="rectangle"
          [border]="true"
          weight="normal"
          kind="primary"
        ></qai-toggle-switch-wrapper>
      </qai-field>

      <qai-field [label]="'simulationBuilder.PAUSE_BUTTON_LABEL' | transloco">
        <qai-toggle-switch-wrapper
          [checked]="pauseEnabledChecked"
          (checkedChange)="onPauseEnabledChange($event)"
          [enabledText]="'simulationBuilder.ENABLED_TEXT' | transloco"
          [disabledText]="'simulationBuilder.DISABLED_TEXT' | transloco"
          size="small"
          shape="rectangle"
          [border]="true"
          weight="normal"
          kind="primary"
          [disabled]="isClosedOrArchived"
        ></qai-toggle-switch-wrapper>
      </qai-field>

      @if (hideResultsFeatureEnabled) {
        <qai-field [label]="'simulationBuilder.HIDE_RESULTS_LABEL' | transloco">
          <qai-toggle-switch-wrapper
            [checked]="hideResultsForLearnersChecked"
            (checkedChange)="onHideResultsForLearnersChange($event)"
            [enabledText]="'simulationBuilder.ENABLED_TEXT' | transloco"
            [disabledText]="'simulationBuilder.DISABLED_TEXT' | transloco"
            size="small"
            shape="rectangle"
            [border]="true"
            weight="normal"
            kind="primary"
          ></qai-toggle-switch-wrapper>
        </qai-field>
      }
    }

    <qai-field [label]="'simulationBuilder.ASSIGNMENT_START_DATE_LABEL' | transloco">
      <qai-datepicker
        [startIcon]="'calendar'"
        [disabled]="false"
        [placeholder]="'simulationBuilder.SELECT_DATE_PLACEHOLDER' | transloco"
        [kind]="'secondary'"
        [shape]="'normal'"
        [size]="'large'"
        [minDate]="minStartDate"
        [value]="form.get('startDate')?.value"
        [displayFormatter]="datePickerFormatter"
        (valueChange)="onDateChange('startDate', $event)"
        [disabled]="readOnly"
      ></qai-datepicker>
    </qai-field>

    <qai-field
      [label]="(isCertificationType() ? 'simulationBuilder.ASSIGNMENT_END_DATE_LABEL' : 'simulationBuilder.ASSIGNMENT_END_DATE_OPTIONAL_LABEL') | transloco"
      [error]="endDateErrorMessage"
    >
      <qai-datepicker
        [startIcon]="'calendar'"
        [disabled]="false"
        [placeholder]="'simulationBuilder.SELECT_DATE_PLACEHOLDER' | transloco"
        [kind]="'secondary'"
        [shape]="'normal'"
        [size]="'large'"
        [minDate]="minEndDate"
        [value]="form.get('endDate')?.value"
        [displayFormatter]="datePickerFormatter"
        (valueChange)="onDateChange('endDate', $event)"
      ></qai-datepicker>
    </qai-field>
  </qai-item-grid>
</div>
